import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { BarChart3, KeyRound, Loader2, Power } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { User } from "@/lib/users.api";
import { AccountStatusBadge } from "./components/AccountStatusBadge";
import { RolesBadge } from "./components/RolesBadges";
import { employeeDetailUrl } from "./usersPage.helpers";

interface CreateUsersColumnsOptions {
  busyId: number | null;
  onEdit: (user: User) => void;
  onResetOtp: (user: User) => void;
  onToggleActivation: (user: User) => void;
}

export function createUsersColumns({
  busyId,
  onEdit,
  onResetOtp,
  onToggleActivation,
}: CreateUsersColumnsOptions): ColumnDef<User, any>[] {
  return [
    {
      accessorKey: "fullName",
      header: "Nom",
      cell: ({ row }) => (
        <button
          type="button"
          className="cursor-pointer text-left text-[12px] text-body hover:text-brand-gold-dark"
          onClick={() => onEdit(row.original)}
        >
          {row.original.fullName}
        </button>
      ),
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => (
        <span className="text-[12px] text-muted-foreground">{row.original.email}</span>
      ),
    },
    {
      accessorKey: "role",
      header: "Rôle",
      cell: ({ row }) => <RolesBadge role={row.original.role} />,
    },
    {
      accessorKey: "active",
      header: "Statut",
      cell: ({ row }) => (
        <AccountStatusBadge active={row.original.active} firstLogin={row.original.firstLogin} />
      ),
    },
    {
      id: "actions",
      header: "Actions",
      meta: { align: "right" },
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="flex justify-end gap-1">
            <Link
              to={employeeDetailUrl(user.id)}
              className={cn(buttonVariants({ variant: "ghost", size: "icon" }))}
              title="Détail employé"
            >
              <BarChart3 size={13} />
            </Link>
            <Button
              variant="ghost"
              size="icon"
              title="Réinitialiser l'OTP"
              disabled={busyId === user.id}
              onClick={() => onResetOtp(user)}
            >
              {busyId === user.id ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <KeyRound size={13} />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title={user.active ? "Désactiver" : "Activer"}
              disabled={busyId === user.id}
              onClick={() => onToggleActivation(user)}
              className={
                user.active
                  ? "text-danger-text hover:bg-danger-bg"
                  : "text-success-text hover:bg-success-bg"
              }
            >
              <Power size={13} />
            </Button>
          </div>
        );
      },
    },
  ];
}
